import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {parsePhoneNumber} from 'libphonenumber-js';
import AppInput from '@components/Core/inputs/AppInput/AppInput';
import {validatePhone} from '@utils/formValidation';
import './smsField.less';

const SMSField = ({phoneNumber, onPhoneNumberChange}) => {
    const intl = useIntl();
    const [isTouched, setIsTouched] = useState(false);

    const handlePhoneNumberChange = (event) => {
        onPhoneNumberChange(event.target.value);
    };

    const handleBlur = () => {
        setIsTouched(true);
    };

    const getPhoneValidation = () => {
        if (!phoneNumber) {
            return {isValid: true, errorMessage: ''};
        }
        return validatePhone(phoneNumber);
    };

    const getIsPhoneNumberValid = () => getPhoneValidation().isValid;

    const getFormattedPhoneNumber = () => {
        if (!phoneNumber) {
            return '';
        }
        try {
            const parsedNumber = parsePhoneNumber(phoneNumber);
            return parsedNumber ? parsedNumber.formatInternational() : phoneNumber;
        } catch (error) {
            return phoneNumber;
        }
    };

    const getHelperText = () => {
        if (!phoneNumber) {
            return intl.formatMessage({
                id: 'oss.components.ReassignAuth.sms.helperText',
                defaultMessage: 'Enter phone number in international format (e.g., +31612345678)'
            });
        }

        const validation = getPhoneValidation();
        if (isTouched && !validation.isValid) {
            return validation.errorMessage;
        }

        if (!validation.isValid) {
            return intl.formatMessage({
                id: 'oss.components.ReassignAuth.sms.invalidFormat',
                defaultMessage: 'Invalid phone number format. Use international format with country code.'
            });
        }

        const formatted = getFormattedPhoneNumber();
        if (formatted !== phoneNumber) {
            return intl.formatMessage(
                {
                    id: 'oss.components.ReassignAuth.sms.formattedNumber',
                    defaultMessage: 'Formatted: {formatted}'
                },
                {formatted}
            );
        }

        return '';
    };

    const isPhoneNumberValid = getIsPhoneNumberValid();
    const shouldShowError = isTouched && !!phoneNumber && !isPhoneNumberValid;

    return (
        <div className="sms-field" data-wdio="test-sms-field">
            <AppInput
                label={intl.formatMessage({
                    id: 'oss.components.ReassignAuth.sms.phoneNumber',
                    defaultMessage: 'Phone Number'
                })}
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                onBlur={handleBlur}
                required
                error={shouldShowError}
                helperText={getHelperText()}
                dataWdio="test-sms-phone-input"
                placeholder={intl.formatMessage({
                    id: 'oss.components.ReassignAuth.sms.placeholder',
                    defaultMessage: '+31612345678'
                })}
                type="tel"
                className="sms-field__input"
            />
            <div className="sms-field__info">
                <p className="sms-field__info-title">
                    {intl.formatMessage({
                        id: 'oss.components.ReassignAuth.sms.infoTitle',
                        defaultMessage: 'SMS Verification'
                    })}
                </p>
                <p className="sms-field__info-text">
                    {intl.formatMessage({
                        id: 'oss.components.ReassignAuth.sms.infoText',
                        defaultMessage: 'The recipient will receive a verification code via SMS '
                            + 'to this phone number before they can access the document.'
                    })}
                </p>
            </div>
        </div>
    );
};

SMSField.propTypes = {
    phoneNumber: PropTypes.string.isRequired,
    onPhoneNumberChange: PropTypes.func.isRequired
};

export default SMSField;
